import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, update, remove, runTransaction, push, set } from 'firebase/database';
import { useAuth } from '../context/AuthContext';
import './RefundManagement.css';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaExclamationTriangle } from 'react-icons/fa';

const Toast = ({ message, type, onRemove }) => {
    const icons = {
        success: <FaCheckCircle />,
        error: <FaExclamationTriangle />,
    };
    return (
        <div className={`toast toast-${type}`}>
            {icons[type]}
            <p>{message}</p>
            <button onClick={onRemove} className="btn-close-toast"><FaTimesCircle /></button>
        </div>
    );
};

const RefundManagement = () => {
    const { currentUser, userData } = useAuth();
    const [refundRequests, setRefundRequests] = useState([]);
    const [refundHistory, setRefundHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);
    const [toasts, setToasts] = useState([]);
    const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'history'

    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const showToast = (message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev.slice(-4), { id, message, type }]);
        setTimeout(() => removeToast(id), 5000);
    };

    useEffect(() => {
        const db = getDatabase();
        const requestsRef = ref(db, 'refundRequests');
        const historyRef = ref(db, 'refundHistory');

        const unsubscribeRequests = onValue(requestsRef, (snapshot) => {
            const data = snapshot.val();
            const loadedRequests = data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : [];
            setRefundRequests(loadedRequests.sort((a, b) => new Date(b.requestedAt) - new Date(a.requestedAt)));
            setLoading(false);
        });

        const unsubscribeHistory = onValue(historyRef, (snapshot) => {
            const data = snapshot.val();
            const loadedHistory = data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : [];
            setRefundHistory(loadedHistory.sort((a, b) => new Date(b.processedAt || b.requestedAt) - new Date(a.processedAt || a.requestedAt)));
        });

        return () => {
            unsubscribeRequests();
            unsubscribeHistory();
        };
    }, []);

    const handleUpdateStatus = async (id, status) => {
        setProcessingId(id);
        const db = getDatabase();
        try {
            await update(ref(db, `refundRequests/${id}`), { status });
            showToast(`Request marked as ${status}.`, 'success');
        } catch (error) {
            showToast('Failed to update status.', 'error');
            console.error("Status update error: ", error);
        } finally {
            setProcessingId(null);
        }
    };
    
    const handleProcessRefund = async (request) => {
        if (!window.confirm("Are you sure you want to process this refund? This will deduct money from the student's wallet.")) return;
        
        setProcessingId(request.id);
        const db = getDatabase();
        const studentWalletRef = ref(db, `users/${request.studentId}/balance`);

        try {
            // 1. Deduct from wallet
            await runTransaction(studentWalletRef, (currentBalance) => {
                const balance = currentBalance || 0;
                if (balance < request.amount) {
                    throw new Error("Insufficient student wallet balance.");
                }
                return balance - request.amount;
            });

            // 2. Add to wallet transactions
            const walletTxnRef = push(ref(db, `walletTransactions/${request.studentId}`));
            await set(walletTxnRef, {
                amt: request.amount,
                type: 'debit',
                desc: `Refund processed: ${request.reason}`,
                method: 'refund',
                ref: `REFUND-${request.id}`,
                date: new Date().toISOString().split('T')[0],
            });

            // 3. Move from requests to history
            const processedRequest = { 
                ...request, 
                status: 'processed', 
                processedAt: new Date().toISOString(),
                processedBy: {
                    uid: currentUser.uid,
                    name: userData.displayName || userData.name
                }
            };
            await set(ref(db, `refundHistory/${request.id}`), processedRequest);
            await remove(ref(db, `refundRequests/${request.id}`));

            showToast('Refund processed successfully!', 'success');

        } catch (error) {
            showToast(`Error processing refund: ${error.message}`, 'error');
            console.error("Process refund error: ", error);
        } finally {
            setProcessingId(null);
        }
    };
    
    if (loading) return <div className="text-center"><FaSpinner className="spinner" /></div>;

    const renderTable = (data, isHistory) => (
        <div className="table-responsive">
            <table className="table table-bordered table-striped">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Student</th>
                        <th>Amount (₦)</th>
                        <th>Reason</th>
                        <th>Bank Details</th>
                        <th>Status</th>
                        <th>{isHistory ? 'Processed By' : 'Actions'}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map(req => (
                        <tr key={req.id}>
                            <td>{new Date(req.requestedAt).toLocaleString()}</td>
                            <td>{req.studentName}</td>
                            <td>{req.amount.toLocaleString()}</td>
                            <td>{req.reason}</td>
                            <td>{req.bankDetails ? `${req.bankDetails.accountName}, ${req.bankDetails.bankName}, ${req.bankDetails.accountNumber}`: 'N/A'}</td>
                            <td><span className={`badge badge-${req.status}`}>{req.status}</span></td>
                            <td>
                                {isHistory ? (
                                    req.processedBy ? `${req.processedBy.name}` : 'N/A'
                                ) : (
                                    <div className="action-buttons">
                                        {processingId === req.id ? <FaSpinner className="spinner" /> : (
                                            <>
                                                {req.status === 'pending' && <button className="btn btn-sm btn-success" onClick={() => handleUpdateStatus(req.id, 'approved')}>Approve</button>}
                                                {req.status === 'pending' && <button className="btn btn-sm btn-danger" onClick={() => handleUpdateStatus(req.id, 'rejected')}>Reject</button>}
                                                {req.status === 'approved' && <button className="btn btn-sm btn-primary" onClick={() => handleProcessRefund(req)}>Process</button>}
                                            </>
                                        )}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="refund-management-container">
            <div className="toast-container-wrapper">
                {toasts.map(toast => (
                    <Toast key={toast.id} message={toast.message} type={toast.type} onRemove={() => removeToast(toast.id)} />
                ))}
            </div>
            
            <h2>Refund Management</h2>
            
            <div className="tabs">
                <button className={`tab ${activeTab === 'requests' ? 'active' : ''}`} onClick={() => setActiveTab('requests')}>
                    Pending Requests ({refundRequests.length})
                </button>
                <button className={`tab ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
                    History ({refundHistory.length})
                </button>
            </div>
            
            {activeTab === 'requests' ? (
                refundRequests.length > 0 ? renderTable(refundRequests, false) : <p>No pending refund requests.</p>
            ) : (
                refundHistory.length > 0 ? renderTable(refundHistory, true) : <p>No refund history.</p>
            )}
        </div>
    );
};

export default RefundManagement;
